import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { initSentry } from 'src/startup';
import { Providers } from './providers';
import { FullPageSpinner } from './components/Spinner';
import App from './App';
import './index.css';
import 'src/services/i18n/i18n';
import { installChunkErrorListener } from 'src/utils/chunk-error-handler';
import { clearCachesAndReload } from 'src/utils/cache-utils';
import { hasReactNativeMessageChannel } from 'src/utils/webview-detection';
import {
  ANDROID_WEBVIEW_CLASS,
  shouldApplyAndroidWebViewScrollFix,
} from 'src/utils/android-webview-scroll.logic';

// Handle Vite preload failures by clearing stale caches before reloading.
// Without cache clearing, the first reload hits stale assets again, wasting a cycle.
// The void prefix marks intentional fire-and-forget; clearCachesAndReload has an
// internal timeout fallback that guarantees reload even if cache clearing stalls.
const PRELOAD_RELOAD_KEY = 'vite_preload_error_reload';
window.addEventListener('vite:preloadError', (event) => {
  const reloadAttempted = sessionStorage.getItem(PRELOAD_RELOAD_KEY);
  if (!reloadAttempted) {
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, 'true');
    event.preventDefault();
    void clearCachesAndReload();
  } else {
    sessionStorage.removeItem(PRELOAD_RELOAD_KEY);
  }
});
sessionStorage.removeItem(PRELOAD_RELOAD_KEY);

// Install global chunk error listener for dynamic import failures
installChunkErrorListener();

initSentry();

// Android WebView cannot touch-scroll the layout while div#root is overflow:hidden; the class
// lets index.css lift that one rule there. See android-webview-scroll.logic.ts.
if (
  shouldApplyAndroidWebViewScrollFix({
    hasNativeMessageChannel: hasReactNativeMessageChannel(),
    userAgent: navigator.userAgent,
  })
) {
  document.documentElement.classList.add(ANDROID_WEBVIEW_CLASS);
}

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <Providers>
      <Suspense fallback={<FullPageSpinner />}>
        <App />
      </Suspense>
    </Providers>
  </React.StrictMode>,
);

// Tell the inline boot watchdog in index.html that the app started. Deliberately set AFTER
// render() is called, not at the top of the module: if initSentry() or createRoot().render()
// throws synchronously the flag stays false, the watchdog stays armed, and the user gets the
// reload/failure panel instead of a spinner that never ends. Anything that fails later is
// covered by the chunk handler and the error boundary.
window.__APP_BOOTED__ = true;

